import { useState, useEffect, useCallback } from "react";
import { RefreshCcw, ScrollText, Target, Server } from "lucide-react";
import { toast } from "sonner";
import { send } from "@/lib/deriv";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

// 📈 HIGH LOW — fully automatic. The ☁️ 24/7 SAFE auto-pilot trades NOTOUCH
// on Vol 100 (1s) with the barrier auto-tuned to pay ~x1.25 (~80% win rate),
// picks its side from live drift, and SIZES recovery trades to win losses
// back +10% (comeback, ON by default). Partial wins only shrink the debt —
// base stake returns once the full target is recovered.
const SYMBOL = "1HZ100V";
const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date().toDateString() === d.toDateString();
    return today ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
};

// 📊 live price chart — 150 ticks, 2.5s refresh, bot entries marked on the
// line, and the bot's current auto-tuned NOTOUCH line drawn when flying
const LiveChart = ({ authorized, srv }) => {
  const [data, setData] = useState(null); // { prices, times }
  const sym = srv?.symbol || SYMBOL;
  useEffect(() => {
    if (!authorized) return undefined;
    let dead = false;
    const run = async () => {
      try {
        const h = await send({ ticks_history: sym, count: 150, end: "latest", style: "ticks" });
        const prices = (h?.history?.prices || []).map(Number);
        const times = (h?.history?.times || []).map(Number);
        if (!dead && prices.length > 10) setData({ prices, times });
      } catch { /* keep old */ }
    };
    run();
    const iv = setInterval(run, 2500);
    return () => { dead = true; clearInterval(iv); };
  }, [authorized, sym]);

  if (!authorized || !data) {
    return (
      <div data-testid="hilo-chart" className="rounded-lg border border-vts-line/40 bg-vts-surface/30 h-56 flex items-center justify-center">
        <p className="text-[10px] font-mono text-vts-silver/45">{authorized ? "loading live chart…" : "Connect Deriv to see the live Vol 100 (1s) chart"}</p>
      </div>
    );
  }
  const { prices, times } = data;
  const min = Math.min(...prices), max = Math.max(...prices);
  const span = max - min || 1;
  const X = (i) => (i / (prices.length - 1)) * 100;
  const Y = (v) => 58 - ((v - min) / span) * 52 - 3;
  const pts = prices.map((v, i) => `${X(i)},${Y(v)}`).join(" ");
  // 📐 moving averages — the exact trend gate the bot trades with
  const maPts = (n) => prices
    .map((_, i) => (i >= n - 1 ? `${X(i)},${Y(prices.slice(i - n + 1, i + 1).reduce((a, b) => a + b, 0) / n)}` : null))
    .filter(Boolean).join(" ");
  const ma10 = maPts(10), ma30 = maPts(30);
  const last = prices[prices.length - 1];
  const drift = last - prices[Math.max(0, prices.length - 20)];
  const up = drift >= 0;
  // bot entries/results mapped onto the chart by tick epoch
  const markers = (srv?.live_log || []).map((e) => {
    const ep = Math.floor(new Date(e.at).getTime() / 1000);
    let idx = -1, bestD = 3;
    times.forEach((t, i) => { const d = Math.abs(t - ep); if (d < bestD) { bestD = d; idx = i; } });
    return idx >= 0 ? { idx, kind: e.kind } : null;
  }).filter(Boolean);
  // the bot's current line (relative barrier off the live price)
  const lineY = srv?.running && srv?.last_barrier ? Y(last + Number(srv.last_barrier)) : null;
  return (
    <div data-testid="hilo-chart" className="rounded-lg border border-vts-line/40 bg-vts-surface/30 p-2">
      <div className="flex items-center justify-between font-mono mb-1 flex-wrap gap-1">
        <span className="text-[9px] uppercase tracking-wider text-vts-silver/50">{srv?.symbol_name || "Vol 100 (1s)"} · live · ⬆ entries · {srv?.running ? "— bot line drawn" : "last 150 ticks"}</span>
        <span data-testid="hilo-chart-price" className={`text-sm font-bold ${up ? "text-vts-profit" : "text-vts-loss"}`}>
          {last.toFixed(2)} {up ? "↗" : "↘"} {drift >= 0 ? "+" : "−"}{Math.abs(drift).toFixed(2)}
        </span>
      </div>
      <svg viewBox="0 0 100 58" className="w-full h-48 sm:h-56" preserveAspectRatio="none">
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1="0" y1={58 * g} x2="100" y2={58 * g} stroke="#334155" strokeWidth="0.2" strokeDasharray="1.5 2" />
        ))}
        <polyline points={`${pts} 100,58 0,58`} fill={up ? "rgba(34,197,94,0.07)" : "rgba(239,68,68,0.07)"} stroke="none" />
        <polyline points={pts} fill="none" stroke={up ? "#22c55e" : "#ef4444"} strokeWidth="0.7" />
        <polyline points={ma10} fill="none" stroke="#f5c542" strokeWidth="0.45" opacity="0.9" />
        <polyline points={ma30} fill="none" stroke="#a78bfa" strokeWidth="0.45" opacity="0.9" />
        {lineY != null && lineY > 0 && lineY < 58 && (
          <>
            <line x1="0" y1={lineY} x2="100" y2={lineY} stroke="#38bdf8" strokeWidth="0.5" strokeDasharray="3 2" />
            <text x="1" y={Math.max(4, lineY - 1.5)} fill="#38bdf8" fontSize="3.4" fontFamily="monospace">bot line {srv.last_barrier} — price must stay {Number(srv.last_barrier) < 0 ? "ABOVE" : "BELOW"} it</text>
          </>
        )}
        {markers.map((m, i) => (
          <circle key={i} cx={X(m.idx)} cy={Y(prices[m.idx])} r="1.4"
            fill={m.kind === "win" ? "#22c55e" : m.kind === "loss" ? "#ef4444" : "#f59e0b"}
            stroke="#0f172a" strokeWidth="0.35" />
        ))}
        <circle cx="100" cy={Y(last)} r="1.2" fill={up ? "#22c55e" : "#ef4444"}>
          <animate attributeName="r" values="1;2;1" dur="1.5s" repeatCount="indefinite" />
        </circle>
      </svg>
      <div className="flex justify-between font-mono text-[8px] text-vts-silver/40 flex-wrap gap-x-2">
        <span>low {min.toFixed(2)}</span>
        <span className="text-amber-300/70">● enter</span>
        <span className="text-vts-profit/70">● win</span>
        <span className="text-vts-loss/70">● loss</span>
        <span><span className="text-yellow-300/80">— MA10</span> <span className="text-violet-300/80">— MA30</span> · bot only enters when they agree</span>
        <span>high {max.toFixed(2)}</span>
      </div>
    </div>
  );
};

export const HigherLow = ({ authorized, accountId, currency = "USD", onConnect }) => {
  const { session } = useAuth();
  const [srv, setSrv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stakeMode, setStakeMode] = useState("pct"); // % of balance default — survives loss streaks
  const [mode, setMode] = useState("safe"); // 🛡️ safe | ⚖️ classic | 🎲 bold
  const [stakePct, setStakePct] = useState(1);
  const [stake, setStake] = useState("1");
  const [slMode, setSlMode] = useState("pct");
  const [slPct, setSlPct] = useState(10);
  const [sl, setSl] = useState("10");
  const [smartTp, setSmartTp] = useState(true); // 🧠 bank target ~1–2% of balance, re-rolled per session
  const [tp, setTp] = useState("5");
  const [autoRestart, setAutoRestart] = useState(true);
  const [srvComeback, setSrvComeback] = useState(true); // 🎯 ON by default (user rule)
  const [strictRec, setStrictRec] = useState(false); // 🛡️ strict = ⅓ of debt per attempt, tighter caps
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get(`/hilo-bot/status/${accountId}`);
      setSrv(data);
    } catch { /* keep old */ }
  }, [accountId]);
  useEffect(() => {
    poll();
    const t = setInterval(poll, srv?.running ? 3000 : 6000); // faster while flying → live feed
    return () => clearInterval(t);
  }, [poll, srv?.running]);

  const fetchLog = useCallback(async () => {
    if (!accountId) return;
    setLogLoading(true);
    try {
      const res = await api.get("/track/trades", { params: { account_id: accountId, strategy: "High Low", limit: 30, skip: 0 } });
      setLog(res.data?.trades || []);
    } catch { /* keep old */ }
    setLogLoading(false);
  }, [accountId]);
  useEffect(() => {
    fetchLog();
    const t = setInterval(fetchLog, 12000);
    return () => clearInterval(t);
  }, [fetchLog]);

  const startBot = async () => {
    if (!authorized) { onConnect?.(); return; }
    setBusy(true);
    try {
      let bal = 0;
      try { const r = await send({ balance: 1 }, { timeout: 6000 }); bal = Number(r?.balance?.balance || 0); } catch { /* server re-reads */ }
      const slVal = slMode === "pct" && bal ? Math.max(1, Math.round(bal * slPct) / 100) : Math.max(1, Number(sl) || 10);
      const tpVal = smartTp && bal ? Math.max(0.5, Math.round(bal * 1.5) / 100) : Math.max(0.5, Number(tp) || 5);
      const { data } = await api.post("/hilo-bot/start", {
        account_id: accountId, access_token: session?.token || "",
        stake: Math.max(0.35, Number(stake) || 1), sl: slVal, tp: tpVal,
        auto_restart: smartTp ? true : autoRestart, comeback: srvComeback,
        recovery_mode: strictRec ? "strict" : "normal",
        stake_pct: stakeMode === "pct" ? stakePct : null,
        sl_pct: slMode === "pct" ? slPct : null, smart_tp: smartTp, mode,
      });
      setSrv(data);
      toast.success("☁️ 24/7 High Low bot started", { description: `${mode.toUpperCase()} · ${stakeMode === "pct" ? `${stakePct}% of balance per trade` : `$${stake} per trade`} · SL −$${slVal.toFixed(2)}${smartTp ? " · 🧠 SMART TP banks & restarts" : ` · TP +$${tpVal.toFixed(2)}`} · 🏦 profit lock on` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    } finally { setBusy(false); }
  };
  const stopBot = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hilo-bot/stop/${accountId}`);
      setSrv(data);
      toast("🛑 24/7 High Low bot stopped");
    } catch { toast.error("Stop failed"); } finally { setBusy(false); }
  };

  const running = srv?.running;
  return (
    <div data-testid="hilo-game" className="rounded-xl border border-vts-line bg-vts-card p-4 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Target className="h-4 w-4 text-sky-400" />
        <div>
          <p className="text-sm font-semibold text-white">High Low — ☁️ 24/7 SAFE auto-pilot</p>
          <p className="text-[10px] text-vts-silver/50 font-mono">Fully automatic on the server: SAFE (~80% win) trades from live drift · 🧠 SMART TP banks ~1–2% of balance & restarts · 🏦 trailing profit lock secures the peak · 🎯 comeback wins the FULL loss back +10% before resetting to base. Keeps flying with your phone off.</p>
        </div>
        {running && <span className="ml-auto flex items-center gap-1.5 rounded-full bg-vts-profit/15 px-2 py-0.5 text-[10px] font-mono font-bold text-vts-profit"><span className="h-1.5 w-1.5 rounded-full bg-vts-profit animate-pulse" /> FLYING</span>}
      </div>

      <LiveChart authorized={authorized} srv={srv} />

      {/* 🔴 LIVE FEED — see the bot enter and settle in real time */}
      {running && (
        <div data-testid="hilo-live-feed" className="rounded-lg border border-vts-line/40 bg-vts-surface/30 p-2 space-y-1">
          <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> Live feed
          </span>
          {srv?.live ? (
            <p data-testid="hilo-live-now" className="rounded bg-amber-500/10 border border-amber-500/40 px-2 py-1.5 font-mono text-[11px] font-bold text-amber-300">
              ⏳ IN TRADE — ${Number(srv.live.stake).toFixed(2)} · {String(srv.live.mode || "safe").toUpperCase()} {srv.live.side === "down" ? "📉" : "📈"}{srv.live.barrier ? ` · line ${srv.live.barrier}` : ""} · pays ×{srv.live.ratio}{srv.live.recovery ? " · 🎯 winning losses back +10%" : ""}
            </p>
          ) : (
            <p className="font-mono text-[10px] text-vts-silver/45">📡 tuning the next line…</p>
          )}
          <div className="max-h-32 overflow-y-auto space-y-0.5">
            {(srv?.live_log || []).map((e, i) => (
              <p key={i} className={`font-mono text-[10px] px-1 ${e.kind === "win" ? "text-vts-profit" : e.kind === "loss" ? "text-vts-loss" : "text-vts-silver/60"}`}>
                <span className="text-vts-silver/35">{fmtTime(e.at)}</span> {e.text}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* ☁️ 24/7 bot controls */}
      <div data-testid="hilo-server-bot" className="rounded-lg border border-vts-line/40 bg-vts-surface/30 p-3 space-y-3">
        {running ? (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 text-center font-mono">
              {[["Net", `${(srv.net ?? 0) >= 0 ? "+" : "−"}$${Math.abs(srv.net ?? 0).toFixed(2)}`, (srv.net ?? 0) >= 0 ? "text-vts-profit" : "text-vts-loss"],
                ["W/L", `${srv.wins ?? 0}/${srv.losses ?? 0}`, "text-white"],
                ["Banked", `$${(srv.banked ?? 0).toFixed(2)}`, "text-amber-300"],
                ["Peak", `+$${(srv.peak_net ?? 0).toFixed(2)}`, "text-sky-300"],
                ["Lock", srv.lock_armed ? `≥$${(srv.lock_floor ?? 0).toFixed(2)}` : "arming…", srv.lock_armed ? "text-amber-300" : "text-vts-silver/50"]].map(([l, v, c]) => (
                <div key={l} className="rounded bg-vts-bg/60 py-1.5">
                  <p className="text-[8px] uppercase tracking-wider text-vts-silver/50">{l}</p>
                  <p data-testid={`hilo-srv-${String(l).toLowerCase()}`} className={`text-xs font-bold ${c}`}>{v}</p>
                </div>
              ))}
            </div>
            {/* 🎯 recovery ledger card — SEE the debt shrink and the reset happen */}
            {(srv.deficit ?? 0) > 0 ? (
              <div data-testid="hilo-recovery-card" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 font-mono text-[10px] text-amber-300 leading-relaxed">
                🎯 <b>COMEBACK — owes ${(srv.deficit ?? 0).toFixed(2)}</b> (full loss +10%). Every win subtracts its profit from this; the stake resets to base <b>${Number(srv.base_stake ?? srv.stake ?? 0).toFixed(2)}</b> only when it hits $0.
              </div>
            ) : (
              <div data-testid="hilo-recovery-card" className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-2 font-mono text-[10px] text-emerald-300">
                ✅ At base stake <b>${Number(srv.base_stake ?? srv.stake ?? 0).toFixed(2)}</b> — nothing to win back.
              </div>
            )}
            <p className="text-[9px] text-vts-silver/45 font-mono">phase: {srv.phase || "…"} · {String(srv.mode || "safe").toUpperCase()} · 🔀 {srv.symbol_name || "Vol 100 (1s)"} · {srv.stake_pct ? `stake ${srv.stake_pct}% of balance${srv.balance ? ` (~$${(srv.balance * srv.stake_pct / 100).toFixed(2)})` : ""}` : `stake $${srv.stake}`} · SL −${srv.sl}{srv.sl_pct ? ` (${srv.sl_pct}% of balance)` : ""} · {srv.smart_tp ? `🧠 SMART TP +$${srv.tp} (re-rolls after every bank)` : `TP +$${srv.tp}${srv.auto_restart ? " (banks & restarts)" : ""}`} · 🎯 comeback {srv.comeback === false ? "OFF" : "ON (+10%)"}{srv.last_ratio ? ` · paying ×${srv.last_ratio}` : ""}</p>
            <button data-testid="hilo-srv-stop" disabled={busy} onClick={stopBot}
              className="w-full rounded-lg border border-vts-loss/50 bg-vts-loss/10 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-vts-loss hover:bg-vts-loss/20 transition">
              🛑 Stop 24/7 bot
            </button>
          </>
        ) : (
          <>
            {srv?.stopped_reason && (
              <p data-testid="hilo-srv-stopped" className={`rounded-md px-2.5 py-1.5 text-[10px] font-mono ${srv.stopped_reason === "take_profit" ? "border border-vts-profit/40 bg-vts-profit/10 text-vts-profit" : "border border-vts-loss/40 bg-vts-loss/10 text-vts-loss"}`}>
                {srv.stopped_reason === "take_profit" ? `🏦 Stopped at take-profit — net +$${(srv.net ?? 0).toFixed(2)} secured` : srv.stopped_reason === "stop_loss" ? `🛑 Stopped at your stop-loss — net −$${Math.abs(srv.net ?? 0).toFixed(2)}` : `Stopped (${srv.stopped_reason})`}
              </p>
            )}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono">Mode</span>
              {[["safe", "🛡️ SAFE", "72% measured · ~×1.22"], ["classic", "⚖️ CLASSIC", "~48% measured · ×1.82"]].map(([id, label, sub]) => (
                <button key={id} data-testid={`hilo-srv-mode-${id}`} onClick={() => setMode(id)}
                  className={`h-9 px-2.5 rounded-lg border font-mono text-[10px] font-bold transition text-left ${mode === id ? "border-sky-400 bg-sky-400/20 text-sky-300" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                  {label}<span className="block text-[7px] font-normal opacity-70">{sub}</span>
                </button>
              ))}
              <span className="text-[8px] font-mono text-vts-silver/40 max-w-[240px]">📐 every mode waits for the MA10/MA30 trend to agree AND skips flat chop before entering · 🎲 BOLD retired (measured 20% wins, −$13.17/30 trades)</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono">Stake</span>
              {[1, 2, 3, 4, 5].map((p) => (
                <button key={p} data-testid={`hilo-srv-pct-${String(p).replace(".", "_")}`}
                  onClick={() => { setStakeMode("pct"); setStakePct(p); }}
                  className={`h-8 px-2.5 rounded-full border font-mono text-[10px] font-bold transition ${stakeMode === "pct" && stakePct === p ? "border-sky-400 bg-sky-400/20 text-sky-300" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                  {p}% of balance
                </button>
              ))}
              <button data-testid="hilo-srv-mode-usd" onClick={() => setStakeMode("usd")}
                className={`h-8 px-2.5 rounded-full border font-mono text-[10px] font-bold transition ${stakeMode === "usd" ? "border-sky-400 bg-sky-400/20 text-sky-300" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                fixed $
              </button>
              {stakeMode === "usd" && (
                <input data-testid="hilo-srv-stake" type="number" min="0.35" value={stake} onChange={(e) => setStake(e.target.value)}
                  className="w-20 rounded-md border border-vts-line bg-vts-surface px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-sky-400" />
              )}
            </div>
            <p className="text-[8px] font-mono text-vts-silver/40">1% is the survivable sweet spot. ⚠️ 3%+ is aggressive — at ~×1.2 payout one loss takes MANY wins to claw back, and comeback needs headroom under the 10%-of-balance recovery cap.</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono">🛑 Stop-loss</span>
              {[10, 20, 30].map((p) => (
                <button key={p} data-testid={`hilo-srv-slpct-${p}`}
                  onClick={() => { setSlMode("pct"); setSlPct(p); }}
                  className={`h-8 px-2.5 rounded-full border font-mono text-[10px] font-bold transition ${slMode === "pct" && slPct === p ? "border-vts-loss bg-vts-loss/20 text-vts-loss" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                  −{p}% of balance
                </button>
              ))}
              <button data-testid="hilo-srv-sl-usd" onClick={() => setSlMode("usd")}
                className={`h-8 px-2.5 rounded-full border font-mono text-[10px] font-bold transition ${slMode === "usd" ? "border-vts-loss bg-vts-loss/20 text-vts-loss" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                fixed $
              </button>
              {slMode === "usd" && (
                <input data-testid="hilo-srv-sl" type="number" min="1" value={sl} onChange={(e) => setSl(e.target.value)}
                  className="w-20 rounded-md border border-vts-line bg-vts-surface px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-vts-loss" />
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button data-testid="hilo-srv-smarttp" onClick={() => setSmartTp((v) => !v)}
                className={`h-8 rounded-full border px-2.5 font-mono text-[10px] font-bold transition ${smartTp ? "border-vts-profit bg-vts-profit/20 text-vts-profit" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                🧠 SMART TP {smartTp ? "ON" : "OFF"}
              </button>
              {!smartTp && (
                <input data-testid="hilo-srv-tp" type="number" min="0.5" value={tp} onChange={(e) => setTp(e.target.value)}
                  className="w-20 rounded-md border border-vts-line bg-vts-surface px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-sky-400" />
              )}
              <span className="text-[8px] font-mono text-vts-silver/40 max-w-[320px]">
                {smartTp ? "banks at ~1–2% of balance (re-rolled after every bank) & restarts itself — like the Rocket's SMART target" : "fixed take-profit $"}
              </span>
            </div>
            <p className="rounded-md border border-emerald-500/25 bg-emerald-500/5 px-2.5 py-1.5 text-[9px] font-mono text-emerald-300/80">
              🏦 <b>Trailing profit lock</b> is always on: the bot tracks its peak profit — if the run gives back 35% of the peak, it banks the rest instead of bleeding it (arms at 40% of the TP target).
            </p>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="hilo-srv-comeback" onClick={() => setSrvComeback((v) => !v)}
                className={`rounded-md border px-2.5 py-1.5 text-[10px] font-bold font-mono transition ${srvComeback ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-vts-line bg-vts-surface text-vts-silver/50"}`}>
                🎯 COMEBACK {srvComeback ? "ON" : "OFF"}
              </button>
              {srvComeback && (
                <button data-testid="hilo-srv-strict" onClick={() => setStrictRec((v) => !v)}
                  title="STRICT recovery: wins the debt back in thirds with tighter stake caps — slower but gentler on the balance"
                  className={`rounded-md border px-2.5 py-1.5 text-[10px] font-bold font-mono transition ${strictRec ? "border-sky-400/50 bg-sky-400/10 text-sky-300" : "border-vts-line bg-vts-surface text-vts-silver/50"}`}>
                  🛡️ STRICT RECOVERY {strictRec ? "ON" : "OFF"}
                </button>
              )}
              {!smartTp && (
                <button data-testid="hilo-srv-autorestart" onClick={() => setAutoRestart((v) => !v)}
                  className={`rounded-md border px-2.5 py-1.5 text-[10px] font-bold font-mono transition ${autoRestart ? "border-amber-400/50 bg-amber-400/10 text-amber-300" : "border-vts-line bg-vts-surface text-vts-silver/50"}`}>
                  🔁 BANK TP & RESTART {autoRestart ? "ON" : "OFF"}
                </button>
              )}
            </div>
            <button data-testid="hilo-srv-start" disabled={busy} onClick={startBot}
              className="w-full rounded-lg bg-sky-500 py-3 font-mono text-xs font-bold uppercase tracking-widest text-black hover:brightness-110 transition">
              {authorized ? "☁️ Start 24/7 SAFE bot" : "Connect Deriv to start"}
            </button>
          </>
        )}
      </div>

      {/* 📜 persistent trade history (server-side, survives reloads) */}
      <div data-testid="hilo-history" className="rounded-lg border border-vts-line/40 bg-vts-surface/30 p-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">
            <ScrollText className="h-3 w-3" /> Bot history {log.length > 0 && `· ${log.length} trades`}
          </span>
          <button data-testid="hilo-history-refresh" onClick={fetchLog} className="text-vts-silver/50 hover:text-white p-1 rounded transition" title="Refresh">
            <RefreshCcw className={`h-3 w-3 ${logLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
        {log.length === 0 ? (
          <p className="text-[10px] text-vts-silver/45 font-mono">{accountId ? "No trades yet — start the 24/7 bot and every trade lands here." : "Connect Deriv to see your bot history."}</p>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-0.5">
            {log.map((t) => {
              const p = Number(t.profit || 0);
              const open = t.status === "open";
              return (
                <div key={t.contract_id} className="flex items-center gap-2 rounded bg-vts-bg/60 px-2 py-1 font-mono text-[10px]">
                  <span className="h-5 px-1.5 rounded border border-sky-400/40 text-sky-300 flex items-center font-bold">SAFE</span>
                  <span className="text-vts-silver/55">${Number(t.stake || 0).toFixed(2)}</span>
                  {t.barrier && <span className="text-vts-silver/40">line {t.barrier}</span>}
                  <span className="ml-auto text-vts-silver/40">{fmtTime(t.created_at)}</span>
                  <span className={`min-w-[58px] text-right font-bold ${open ? "text-amber-300" : p >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>
                    {open ? "…live" : `${p >= 0 ? "+" : "−"}$${Math.abs(p).toFixed(2)}`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-[9px] text-vts-silver/40 font-mono leading-relaxed">
        SAFE wins when the price stays on your side of an auto-tuned line — the bot re-tunes the line before every trade using Deriv's real live quotes (~×1.25). Win rate is an estimate; the stop-loss is your hard floor. Play for fun, stake what you can afford.
      </p>
    </div>
  );
};

export default HigherLow;
